import { scaleToMax, shareOf } from '@/lib/issuingCharts';
import { cn } from '@/lib/utils';
import { ChartEmpty } from './ChartEmpty';

/**
 * A ranked medicine. `unit` is per row rather than per list because it is: a
 * clinic issues one medicine in tablets and the next in millilitres, and a
 * single list-wide formatter cannot say so.
 */
export interface TopItemRow {
  readonly id: string;
  readonly label: string;
  /** Already-resolved secondary text — the generic name and strength. */
  readonly sublabel?: string;
  readonly value: number;
  /** Already-translated unit for `value`, where the measure has one. */
  readonly unit?: string;
}

interface TopItemsListProps {
  /** Already ranked by the server — this list draws the order it is given. */
  rows: readonly TopItemRow[];
  /**
   * Denominator for the share column: the *whole* period's figure, from the
   * totals panel.
   *
   * Not the sum of these rows. The panel is truncated to its top ten server-side,
   * so a sum of what arrived would make ten rows share out exactly 100% of a
   * total they are only part of — every percentage wrong, and wrong in a way that
   * looks plausible.
   */
  total: number;
  formatValue: (value: number) => string;
  /** Renders a share as text. Injected for the same reason `formatValue` is. */
  formatPercent: (share: number) => string;
  /** The medicine the whole page is currently narrowed to, if any. */
  selectedId: string | null;
  onSelect: (itemId: string) => void;
  ariaLabel: string;
  shareLabel: string;
  /** Qualifies the share column, where the measure behind it needs qualifying. */
  shareNote?: React.ReactNode;
  /** Accessible name for a row's button, given the medicine's name. */
  selectLabel: (name: string) => string;
  emptyLabel: string;
}

/**
 * The ranked list, where every row is the way in to the specific-medicine
 * filter.
 *
 * The bar shows share of the leader, the percentage shows share of all issuing.
 * Two denominators on purpose: the bar answers "how far behind the top medicine
 * is this?", the percentage answers "how much of everything is this?", and one
 * number cannot do both.
 *
 * Each row is a `button` with `aria-pressed`, not a list item with a click
 * handler: selecting a medicine changes every panel on the page, which is a
 * control, and a control has to be reachable from the keyboard and announce
 * whether it is on.
 */
export function TopItemsList({
  rows,
  total,
  formatValue,
  formatPercent,
  selectedId,
  onSelect,
  ariaLabel,
  shareLabel,
  shareNote,
  selectLabel,
  emptyLabel,
}: TopItemsListProps) {
  if (rows.length === 0) return <ChartEmpty label={emptyLabel} />;

  const values = rows.map((row) => row.value);
  const leaderShares = scaleToMax(values);
  const periodShares = shareOf(values, total);

  return (
    <ul aria-label={ariaLabel} className="flex min-w-0 flex-col">
      {rows.map((row, i) => {
        const selected = row.id === selectedId;
        return (
          <li key={row.id} className="min-w-0">
            <button
              type="button"
              aria-pressed={selected}
              aria-label={selectLabel(row.label)}
              onClick={() => onSelect(row.id)}
              className={cn(
                'w-full rounded-md px-2 py-2 text-left transition-colors hover:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
                selected && 'bg-paper-2',
              )}
            >
              <div className="flex items-baseline gap-2">
                <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                  {row.label}
                  {row.sublabel && (
                    <span className="ml-1.5 text-[11px] text-muted">{row.sublabel}</span>
                  )}
                </span>
                <span className="font-mono text-[12.5px] tabular-nums text-ink">
                  {formatValue(row.value)}
                </span>
                {row.unit && <span className="text-[11px] text-muted">{row.unit}</span>}
                <span className="w-12 shrink-0 text-right font-mono text-[11px] tabular-nums text-muted-2">
                  {formatPercent(periodShares[i] ?? 0)}
                </span>
              </div>
              <div
                aria-hidden="true"
                data-testid="bar-track"
                className="mt-1 h-1.5 overflow-hidden rounded-full bg-paper-3"
              >
                <div
                  data-testid="bar-fill"
                  className="h-full rounded-full bg-accent"
                  style={{ width: `${leaderShares[i] ?? 0}%` }}
                />
              </div>
            </button>
          </li>
        );
      })}
      {/* Names what the right-hand percentage is a share of — two denominators
          are in play and the column is too narrow to say so itself — and, where
          that share is of something the caller had to qualify, says that too. */}
      <li className="mt-1.5 flex flex-col items-end gap-1 px-2 text-right text-[11px] text-muted-2">
        {shareLabel}
        {shareNote}
      </li>
    </ul>
  );
}
